/**
 * ChunkErrorBoundary (power#1298)
 *
 * React error boundary that catches chunk load failures from React.lazy routes.
 * When a chunk fails to load (stale deploy, network error), this boundary:
 *
 * 1. Calls handleChunkLoadError to check for a new version and surface the banner
 * 2. Renders a fallback UI with an immediate reload option
 * 3. Also renders NewVersionBanner which appears once the version check completes
 *
 * The fallback reload button is a simple window.location.reload() for immediate
 * recovery. The NewVersionBanner provides the "proper" flow with unsaved-work
 * checks once the async version check finishes.
 */

import React from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NewVersionBanner } from "./NewVersionBanner";
import {
  handleChunkLoadError,
  isChunkLoadError,
} from "@/utils/chunkErrorRecovery";

interface Props {
  children: React.ReactNode;
  /** Optional fallback for non-chunk errors */
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

export class ChunkErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (isChunkLoadError(error)) {
      // Fire async version check - don't await, let it update store
      handleChunkLoadError(error);
    } else {
      // Log non-chunk errors for debugging
      console.error(
        "ChunkErrorBoundary caught non-chunk error:",
        error,
        errorInfo,
      );
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        // Chunk error fallback - centered page state with blue info theme
        return (
          <div className="flex flex-col items-center justify-center h-full gap-6 p-8">
            <div className="text-center space-y-2">
              <RefreshCw className="h-8 w-8 text-[#6ab6f5] mx-auto" />
              <h2 className="text-lg font-medium">This page failed to load</h2>
              <p className="text-muted-foreground text-sm">
                A new version may be available.
              </p>
            </div>

            {/* Immediate fallback - simple reload */}
            <Button
              onClick={this.handleReload}
              className="bg-[#0066CC] hover:bg-[#0052A3] text-white"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reload page
            </Button>

            {/* Banner appears here once store updates - has full unsaved work logic */}
            <NewVersionBanner />
          </div>
        );
      }

      // Non-chunk error - use provided fallback or default
      return (
        this.props.fallback ?? (
          <div className="flex flex-col items-center justify-center h-full p-8">
            <h2 className="text-lg font-medium">Something went wrong</h2>
            <p className="text-muted-foreground text-sm mt-2">
              Please try refreshing the page.
            </p>
            <Button
              onClick={this.handleReload}
              variant="outline"
              className="mt-4"
            >
              Refresh
            </Button>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
